import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/LanguageContext";

interface SEOHeadProps {
  title?: string;
  description?: string;
  ogImage?: string;
  ogUrl?: string;
  type?: string;
  noindex?: boolean;
  /** Path without language prefix, e.g. "/" or "/blog" */
  canonicalPath?: string;
}

export function SEOHead({
  title = "reelistiq — Micro Drama Agency for Brands",
  description = "reelistiq is a Micro Drama Agency from Germany. We help brands reach millions through serialized storytelling on TikTok, Instagram Reels and social media platforms.",
  ogImage = "https://reelistiq.com/og-image.png",
  ogUrl = "https://reelistiq.com",
  type = "website",
  noindex = false,
  canonicalPath,
}: SEOHeadProps) {
  const baseUrl = "https://reelistiq.com";
  const { language } = useLanguage();

  // Build canonical URL: must exactly match the corresponding hreflang URL
  const langPrefix = `/${language}`;
  const pathSuffix = canonicalPath && canonicalPath !== "/" ? canonicalPath : "";
  const canonicalUrl = canonicalPath ? `${baseUrl}${langPrefix}${pathSuffix}` : ogUrl;

  return (
    <Helmet>
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={language === "de" ? "de_DE" : "en_US"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* hreflang alternates — canonical must exactly match the current lang hreflang */}
      {canonicalPath && (
        <>
          <link rel="canonical" href={canonicalUrl} />
          <link rel="alternate" hrefLang="de" href={`${baseUrl}/de${pathSuffix}`} />
          <link rel="alternate" hrefLang="en" href={`${baseUrl}/en${pathSuffix}`} />
          <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/de${pathSuffix}`} />
        </>
      )}
    </Helmet>
  );
}
